<!-- FortiGate fleet overview: the template of FortinetFleetComponent.
     The chart <div>s are filled by ECharts from the component, everything
     else is data-bound. Clicking a device (row, issue, license) emits
     openDevice with its serial, and "Show" on an issue emits openIssue. -->
<div class="fleet-grid">

  <div class="card span12 fleet-error" *ngIf="errorText && !loadedOnce">
    <div class="empty">Could not load the device overview. {{ errorText }}</div>
  </div>
  <div class="card span12" *ngIf="!loadedOnce && !errorText">
    <div class="empty">Collecting every device…</div>
  </div>

  <ng-container *ngIf="loadedOnce">
  <!-- KPI row: always the whole fleet, whatever the filters.
       The id="fleet-*" anchors are the targets of the page sections in the
       application shell's vertical navigation. -->
  <div class="kpi-row span12" id="fleet-top">
    <div class="card kpi" *ngFor="let kpi of kpis; trackBy: trackByKpi">
      <div class="card-block">
        <div class="kpi-label">{{ kpi.label }}</div>
        <div class="kpi-value">
          {{ kpi.value }}<span class="trend kpi-suffix" *ngIf="kpi.suffix">{{ kpi.suffix }}</span>
        </div>
        <div class="kpi-sub">
          <span *ngIf="kpi.badge" class="label" [ngClass]="kpi.badge.cls">{{ kpi.badge.text }}</span>
          <ng-container *ngIf="!kpi.badge">{{ kpi.sub }}</ng-container>
        </div>
      </div>
    </div>
  </div>

  <!-- Filters: narrow the table, the charts and the lists below -->
  <div class="card span12">
    <div class="toolbar fleet-filters" role="search">
      <label class="search">
        <span class="sr-only">Find a device</span>
        <input type="search" placeholder="Find a device: name, serial, group, version"
               [value]="filterText"
               (input)="setFilterText($any($event.target).value)">
      </label>
      <span class="btn-group" role="group" aria-label="Device state">
        <button *ngFor="let option of stateFilters" type="button"
                [class.active]="stateFilter === option.id"
                [attr.aria-pressed]="stateFilter === option.id"
                (click)="setStateFilter(option.id)">{{ option.label }}</button>
      </span>
      <label class="field" *ngIf="showGroups">
        <span class="field-label">Group</span>
        <select [value]="groupFilter === null ? '*' : groupFilter"
                (change)="setGroupFilter($any($event.target).value === '*' ? null : $any($event.target).value)">
          <option value="*" [selected]="groupFilter === null">All groups</option>
          <!-- a select can't hold HTML: the indent of a sub-group is made of non-breaking spaces -->
          <option *ngFor="let group of groups; trackBy: trackByGroup" [value]="group.name"
                  [selected]="groupFilter === group.name">{{ indentText(group.depth) }}{{ group.label }}</option>
        </select>
      </label>
      <span class="hdr-note" *ngIf="filtered">
        {{ rows.length }} of {{ rowsTotal }} devices shown.
        <button type="button" class="link-btn" (click)="clearFilters()">Clear filters</button>
      </span>
      <span class="hdr-note" *ngIf="!filtered">Filters apply to everything below the figures.</span>
    </div>
  </div>

  <!-- Per-group rollup (config.yaml `groups:`); a row filters the page.
       A device counts in each of its groups and in the groups above them. -->
  <div class="card span12" *ngIf="showGroups">
    <div class="card-header">Groups
      <span class="hdr-note">select a group to focus the page on it. A device in several groups counts in each of them,
        so the rows don't add up to the fleet total.</span>
    </div>
    <div class="tbl-wrap">
      <table class="clr sortable">
        <thead>
          <tr>
            <th [attr.aria-sort]="groupSort.ariaSort('label')">
              <button type="button" class="sort-btn" (click)="sortTable(groupSort, 'label')">Group <span class="sort-mark" aria-hidden="true">{{ groupSort.mark('label') }}</span></button></th>
            <th class="num" [attr.aria-sort]="groupSort.ariaSort('devices')">
              <button type="button" class="sort-btn" (click)="sortTable(groupSort, 'devices')">Devices <span class="sort-mark" aria-hidden="true">{{ groupSort.mark('devices') }}</span></button></th>
            <th class="num" [attr.aria-sort]="groupSort.ariaSort('reporting')">
              <button type="button" class="sort-btn" (click)="sortTable(groupSort, 'reporting')">Reporting <span class="sort-mark" aria-hidden="true">{{ groupSort.mark('reporting') }}</span></button></th>
            <th class="num" [attr.aria-sort]="groupSort.ariaSort('attention')">
              <button type="button" class="sort-btn" (click)="sortTable(groupSort, 'attention')">Need attention <span class="sort-mark" aria-hidden="true">{{ groupSort.mark('attention') }}</span></button></th>
            <th class="num" [attr.aria-sort]="groupSort.ariaSort('issues')">
              <button type="button" class="sort-btn" (click)="sortTable(groupSort, 'issues')">Open issues <span class="sort-mark" aria-hidden="true">{{ groupSort.mark('issues') }}</span></button></th>
            <th class="num" [attr.aria-sort]="groupSort.ariaSort('throughput')">
              <button type="button" class="sort-btn" (click)="sortTable(groupSort, 'throughput')">Throughput <span class="sort-mark" aria-hidden="true">{{ groupSort.mark('throughput') }}</span></button></th>
            <th class="num" [attr.aria-sort]="groupSort.ariaSort('sessions')">
              <button type="button" class="sort-btn" (click)="sortTable(groupSort, 'sessions')">Sessions <span class="sort-mark" aria-hidden="true">{{ groupSort.mark('sessions') }}</span></button></th>
          </tr>
        </thead>
        <tbody>
          <tr *ngFor="let group of groups; trackBy: trackByGroup"
              class="dev-row" [class.row-selected]="groupFilter === group.name"
              (click)="setGroupFilter(groupFilter === group.name ? null : group.name)">
            <!-- a sub-group is pushed to the right, 1.2rem per level -->
            <td [style.padding-left.rem]="0.6 + group.depth * 1.2">
              <button type="button" class="dev-link" [title]="group.pathText"
                      [attr.aria-pressed]="groupFilter === group.name"
                      (click)="setGroupFilter(groupFilter === group.name ? null : group.name); $event.stopPropagation()">{{ group.label }}</button>
            </td>
            <td class="num">{{ group.devices }}</td>
            <td class="num" [class.err-warn]="group.reporting < group.devices">{{ group.reporting }}</td>
            <td class="num" [class.util-warn]="group.attention > 0" [class.dim]="!group.attention">{{ group.attention }}</td>
            <td class="num" [class.dim]="!group.issues">{{ group.issues }}</td>
            <td class="num">{{ fmtBps(group.throughput) }}</td>
            <td class="num">{{ fmtNum(group.sessions) }}</td>
          </tr>
        </tbody>
      </table>
    </div>
  </div>

  <!-- Device table: the heart of the page -->
  <div class="card span12" id="fleet-devices">
    <div class="card-header has-filter">
      <span class="card-title">Devices</span>
      <span class="hdr-note push-right">latest poll of each device; select a row to open it</span>
      <button type="button" class="link-btn" [disabled]="!rows.length"
              title="Download the table as shown (filters and order included)"
              (click)="exportDevicesCsv()">CSV</button>
    </div>
    <!-- Whole fleet by state; a segment or its legend entry filters -->
    <div class="state-strip" *ngIf="stateSegs.length">
      <div class="state-bar" role="group" aria-label="Devices by state">
        <button type="button" *ngFor="let segment of stateSegs; trackBy: trackByState"
                class="state-seg" [ngClass]="'seg-' + segment.state"
                [class.dimmed]="exactState !== null && exactState !== segment.state"
                [style.flex-grow]="segment.pct"
                [attr.aria-pressed]="exactState === segment.state"
                [attr.aria-label]="segment.count + ' ' + segment.label + '. Show only these devices'"
                [title]="segment.count + ' ' + segment.label"
                (click)="setExactState(segment.state)"></button>
      </div>
      <span class="state-legend">
        <button type="button" *ngFor="let segment of stateSegs; trackBy: trackByState"
                class="state-key" [attr.aria-pressed]="exactState === segment.state"
                (click)="setExactState(segment.state)">
          <span class="dot" [ngClass]="'seg-' + segment.state"></span>
          <span class="state-key-text"><b>{{ segment.count }}</b> {{ segment.label }}</span>
        </button>
      </span>
    </div>
    <div class="tbl-wrap fleet-tbl-wrap">
      <div class="empty" *ngIf="!rows.length && !rowsTotal">No device has written data yet.</div>
      <div class="empty" *ngIf="!rows.length && rowsTotal">No device matches the filters.
        <button type="button" class="link-btn" (click)="clearFilters()">Clear filters</button></div>
      <table class="clr fleet-tbl sortable" *ngIf="rows.length">
        <thead>
          <tr>
            <th [attr.aria-sort]="deviceSort.ariaSort('label')">
              <button type="button" class="sort-btn" (click)="sortTable(deviceSort, 'label')">Device <span class="sort-mark" aria-hidden="true">{{ deviceSort.mark('label') }}</span></button></th>
            <th *ngIf="showGroups" [attr.aria-sort]="deviceSort.ariaSort('group')">
              <button type="button" class="sort-btn" (click)="sortTable(deviceSort, 'group')">Groups <span class="sort-mark" aria-hidden="true">{{ deviceSort.mark('group') }}</span></button></th>
            <th [attr.aria-sort]="deviceSort.ariaSort('state')">
              <button type="button" class="sort-btn" (click)="sortTable(deviceSort, 'state')">State <span class="sort-mark" aria-hidden="true">{{ deviceSort.mark('state') }}</span></button></th>
            <th [attr.aria-sort]="deviceSort.ariaSort('version')">
              <button type="button" class="sort-btn" (click)="sortTable(deviceSort, 'version')">FortiOS <span class="sort-mark" aria-hidden="true">{{ deviceSort.mark('version') }}</span></button></th>
            <th class="num" [attr.aria-sort]="deviceSort.ariaSort('poll_age')">
              <button type="button" class="sort-btn" (click)="sortTable(deviceSort, 'poll_age')">Last poll <span class="sort-mark" aria-hidden="true">{{ deviceSort.mark('poll_age') }}</span></button></th>
            <th class="num" [attr.aria-sort]="deviceSort.ariaSort('throughput_bps')">
              <button type="button" class="sort-btn" (click)="sortTable(deviceSort, 'throughput_bps')">Throughput <span class="sort-mark" aria-hidden="true">{{ deviceSort.mark('throughput_bps') }}</span></button></th>
            <th [attr.aria-sort]="deviceSort.ariaSort('trend')">
              <button type="button" class="sort-btn" (click)="sortTable(deviceSort, 'trend')">Trend <span class="sort-mark" aria-hidden="true">{{ deviceSort.mark('trend') }}</span></button></th>
            <th class="num" [attr.aria-sort]="deviceSort.ariaSort('sessions')">
              <button type="button" class="sort-btn" (click)="sortTable(deviceSort, 'sessions')">Sessions <span class="sort-mark" aria-hidden="true">{{ deviceSort.mark('sessions') }}</span></button></th>
            <th class="num" [attr.aria-sort]="deviceSort.ariaSort('cpu')">
              <button type="button" class="sort-btn" (click)="sortTable(deviceSort, 'cpu')">CPU <span class="sort-mark" aria-hidden="true">{{ deviceSort.mark('cpu') }}</span></button></th>
            <th class="num" [attr.aria-sort]="deviceSort.ariaSort('mem')">
              <button type="button" class="sort-btn" (click)="sortTable(deviceSort, 'mem')">Memory <span class="sort-mark" aria-hidden="true">{{ deviceSort.mark('mem') }}</span></button></th>
            <th class="num" [attr.aria-sort]="deviceSort.ariaSort('ifaces')">
              <button type="button" class="sort-btn" (click)="sortTable(deviceSort, 'ifaces')">Interfaces up <span class="sort-mark" aria-hidden="true">{{ deviceSort.mark('ifaces') }}</span></button></th>
            <th class="num" [attr.aria-sort]="deviceSort.ariaSort('tunnels')">
              <button type="button" class="sort-btn" (click)="sortTable(deviceSort, 'tunnels')">IPsec up <span class="sort-mark" aria-hidden="true">{{ deviceSort.mark('tunnels') }}</span></button></th>
            <th class="num" *ngIf="showSdwan" [attr.aria-sort]="deviceSort.ariaSort('sdwan')">
              <button type="button" class="sort-btn" (click)="sortTable(deviceSort, 'sdwan')">SD-WAN up <span class="sort-mark" aria-hidden="true">{{ deviceSort.mark('sdwan') }}</span></button></th>
            <th *ngIf="showHa" [attr.aria-sort]="deviceSort.ariaSort('ha')">
              <button type="button" class="sort-btn" (click)="sortTable(deviceSort, 'ha')">HA <span class="sort-mark" aria-hidden="true">{{ deviceSort.mark('ha') }}</span></button></th>
            <th [attr.aria-sort]="deviceSort.ariaSort('issues')">
              <button type="button" class="sort-btn" (click)="sortTable(deviceSort, 'issues')">Issues <span class="sort-mark" aria-hidden="true">{{ deviceSort.mark('issues') }}</span></button></th>
            <th class="num" [attr.aria-sort]="deviceSort.ariaSort('license_min_days')">
              <button type="button" class="sort-btn" (click)="sortTable(deviceSort, 'license_min_days')">License <span class="sort-mark" aria-hidden="true">{{ deviceSort.mark('license_min_days') }}</span></button></th>
          </tr>
        </thead>
        <tbody>
          <tr *ngFor="let row of rows; trackBy: trackBySerial"
              class="dev-row" [class.dev-off]="row.offline"
              (click)="open(row.device.serial)">
            <td>
              <button type="button" class="dev-link"
                      (click)="open(row.device.serial); $event.stopPropagation()">{{ row.device.label }}</button>
              <div class="dev-serial">{{ row.device.hostname ? row.device.hostname + ', ' : '' }}{{ row.device.host ? row.device.serial : '' }}</div>
            </td>
            <!-- main group first (in bold), then the other groups -->
            <td *ngIf="showGroups">
              <ng-container *ngIf="!row.groupTexts.length">-</ng-container>
              <div *ngFor="let groupText of row.groupTexts; let first = first"
                   [class.main-group]="first" [title]="first ? 'main group' : ''">{{ groupText }}</div>
            </td>
            <td class="keep"><span class="label" [ngClass]="row.state.cls"
                      [title]="row.device.top_issue?.title || ''">{{ row.state.text }}</span>
              <span class="label label-count" *ngIf="row.stateMore"
                    [ngClass]="row.stateMore.cls"
                    [title]="row.stateMore.title">{{ row.stateMore.text }}</span></td>
            <td>
              {{ row.device.version || '-' }}
              <span class="label" *ngIf="row.firmware" [ngClass]="row.firmware.cls"
                    [title]="row.firmwareTitle">{{ row.firmware.text }}</span>
              <span class="label label-info" *ngIf="row.device.upgrade && !row.firmware"
                    [title]="'Upgrade path available' + (row.device.firmware_latest ? ' up to ' + row.device.firmware_latest : '')">upgrade</span>
            </td>
            <td class="num" [ngClass]="row.pollCls" [title]="row.ageTitle">{{ row.age }} ago</td>
            <td class="num">{{ row.throughput }}</td>
            <td class="spark-cell">
              <svg *ngIf="row.spark" class="spark" viewBox="0 0 72 18" width="72" height="18"
                   role="img" [attr.aria-label]="row.sparkTitle">
                <title>{{ row.sparkTitle }}</title>
                <path [attr.d]="row.spark"></path>
              </svg>
            </td>
            <td class="num">{{ row.sessions }}</td>
            <td class="num" [ngClass]="row.cpuCls">{{ row.cpu }}</td>
            <td class="num" [ngClass]="row.memCls">{{ row.mem }}</td>
            <td class="num" [ngClass]="row.ifacesCls">{{ row.ifaces }}</td>
            <td class="num" [ngClass]="row.tunnelsCls">{{ row.tunnels }}</td>
            <td class="num" *ngIf="showSdwan" [ngClass]="row.sdwanCls">{{ row.sdwan }}</td>
            <td *ngIf="showHa">
              <span class="label" *ngIf="row.ha" [ngClass]="row.ha.cls">{{ row.ha.text }}</span>
              <span class="dim" *ngIf="!row.ha">standalone</span>
            </td>
            <td class="issue-counts">
              <span class="label label-danger" *ngIf="row.device.issues.danger">{{ row.device.issues.danger }}</span>
              <span class="label label-warning" *ngIf="row.device.issues.warning">{{ row.device.issues.warning }}</span>
              <span class="label label-info" *ngIf="row.device.issues.info">{{ row.device.issues.info }}</span>
              <span class="label label-count" *ngIf="row.acked"
                    [ngClass]="row.ackedCls"
                    [title]="row.ackedTitle">✓ {{ row.acked }}</span>
              <span class="dim" *ngIf="!row.device.issues.danger && !row.device.issues.warning && !row.device.issues.info && !row.acked">none</span>
            </td>
            <td class="num" [ngClass]="row.licenseCls" [title]="row.licenseTitle">{{ row.license }}</td>
          </tr>
        </tbody>
      </table>
    </div>
  </div>

  <!-- Side by side comparisons: one colour per device on every chart.
       Ctrl + scroll or the zoom tool zooms all four together. -->
  <div class="card span6">
    <div class="card-header">Throughput per device
      <span class="hdr-note">{{ chartNotes['throughput'] || 'RX + TX summed over all interfaces' }}</span>
    </div>
    <div class="card-block"><div id="fl-throughput" class="chart"></div></div>
  </div>
  <div class="card span6">
    <div class="card-header">Active sessions per device
      <span class="hdr-note" *ngIf="chartNotes['sessions']">{{ chartNotes['sessions'] }}</span>
    </div>
    <div class="card-block"><div id="fl-sessions" class="chart"></div></div>
  </div>
  <div class="card span6">
    <div class="card-header">CPU per device
      <span class="hdr-note" *ngIf="chartNotes['cpu']">{{ chartNotes['cpu'] }}</span>
    </div>
    <div class="card-block"><div id="fl-cpu" class="chart"></div></div>
  </div>
  <div class="card span6">
    <div class="card-header">Memory per device
      <span class="hdr-note" *ngIf="chartNotes['mem']">{{ chartNotes['mem'] }}</span>
    </div>
    <div class="card-block"><div id="fl-mem" class="chart"></div></div>
  </div>

  <!-- Where attention is needed -->
  <div class="card span6" id="fleet-issues">
    <div class="card-header has-tabs">
      <span class="card-title">Issues across devices</span>
      <div class="tabs" role="tablist" *ngIf="ackSupported"
           (keydown)="onIssuesTabKey($event, tabOpen, tabAcked)">
        <button #tabOpen type="button" class="tab" role="tab"
                [id]="tabIdPrefix + '-open'" [attr.aria-controls]="tabIdPrefix + '-panel'"
                [attr.aria-selected]="issuesTab === 'open'"
                [tabIndex]="issuesTab === 'open' ? 0 : -1"
                (click)="selectIssuesTab('open')">Open
          <span class="tab-count">{{ issuesTotal }}</span></button>
        <button #tabAcked type="button" class="tab" role="tab"
                [id]="tabIdPrefix + '-acked'" [attr.aria-controls]="tabIdPrefix + '-panel'"
                [attr.aria-selected]="issuesTab === 'acked'"
                [tabIndex]="issuesTab === 'acked' ? 0 : -1"
                (click)="selectIssuesTab('acked')">Acknowledged
          <span class="tab-count">{{ issuesAckedTotal }}</span></button>
      </div>
      <span class="hdr-note" *ngIf="issuesTab === 'open' && issuesTotal > issues.length">showing
        {{ issues.length }} of {{ issuesTotal }}</span>
      <span class="hdr-note" *ngIf="issuesTab === 'acked' && issuesAckedTotal > issuesAcked.length">showing
        {{ issuesAcked.length }} of {{ issuesAckedTotal }}</span>
    </div>
    <div class="issues fleet-issues" [attr.role]="ackSupported ? 'tabpanel' : null"
         [id]="tabIdPrefix + '-panel'"
         [attr.aria-labelledby]="ackSupported ? tabIdPrefix + '-' + issuesTab : null">
      <ng-container *ngIf="issuesTab === 'open'">
      <div class="ok" *ngIf="!issues.length">
        <span class="label label-success">{{ filtered ? 'No open issues on the devices shown'
          : issuesAcked.length ? 'No open issues on any device' : 'No issues on any device' }}</span>
      </div>
      <div class="issue" *ngFor="let issue of issues; trackBy: trackByIssue">
        <label class="ack-box" *ngIf="ackSupported && issue.id"
               title="Acknowledge: move it to the Acknowledged tab">
          <input type="checkbox" [checked]="false"
                 [disabled]="ackPending.has(issueKey(issue))"
                 [attr.aria-label]="'Acknowledge: ' + issue.title"
                 (change)="setAck(issue, true)">
        </label>
        <span class="label" [ngClass]="issueBadge(issue.sev).cls">{{ issueBadge(issue.sev).text }}</span>
        <span class="issue-body">
          <button type="button" class="dev-link small" (click)="open(issue.serial)">{{ issue.device }}</button>
          <span class="issue-title">{{ issue.title }}</span>
          <button type="button" class="link-btn ev-link" *ngIf="hasEvidence(issue)"
                  [attr.aria-label]="'Show the data behind: ' + issue.title"
                  (click)="showIssue(issue)">Show</button>
          <ng-container *ngIf="issue.detail">
            <br><span class="issue-detail">{{ issue.detail }}</span>
          </ng-container>
          <ng-container *ngIf="firstSeenText(issue) as since">
            <br><span class="issue-since" [title]="firstSeenTitle(issue)">{{ since }}</span>
          </ng-container>
          <ng-container *ngIf="ackNote(issue, true) as note">
            <br><span class="ack-note ack-note-worse">{{ note }}<ng-container
              *ngIf="ackNoteLine(issue) as line"> {{ line }}</ng-container></span>
          </ng-container>
        </span>
      </div>
      </ng-container>
      <ng-container *ngIf="issuesTab === 'acked'">
        <div class="empty" *ngIf="!issuesAcked.length">Nothing acknowledged.
          Tick an issue in Open to move it here.</div>
        <div class="issue" *ngFor="let issue of issuesAcked; trackBy: trackByIssue">
          <label class="ack-box" title="Move it back to the Open tab">
            <input type="checkbox" [checked]="true"
                   [disabled]="ackPending.has(issueKey(issue))"
                   [attr.aria-label]="'Remove acknowledgement: ' + issue.title"
                   (change)="setAck(issue, false)">
          </label>
          <span class="label" [ngClass]="issueBadge(issue.sev).cls">{{ issueBadge(issue.sev).text }}</span>
          <span class="issue-body">
            <button type="button" class="dev-link small" (click)="open(issue.serial)">{{ issue.device }}</button>
            <span class="issue-title">{{ issue.title }}</span>
            <button type="button" class="link-btn ev-link" *ngIf="issue.id && noteEd.key !== issueKey(issue)"
                    [attr.aria-label]="(issue.ack_note ? 'Edit the note of: ' : 'Add a note to: ') + issue.title"
                    (click)="openNote(issue)">{{ issue.ack_note ? 'Edit note' : 'Note' }}</button>
            <br><span class="ack-note">{{ ackNote(issue, false) }}</span>
            <ng-container *ngIf="ackNoteLine(issue) as line">
              <br><span class="ack-note-text" [title]="issue.ack_note_at ? 'Written ' + fmtStamp(issue.ack_note_at) : ''">{{ line }}</span>
            </ng-container>
            <form class="note-form" *ngIf="noteEd.key === issueKey(issue)" (submit)="$event.preventDefault(); saveNote(issue)">
              <label class="sr-only" [for]="tabIdPrefix + '-note'">Why is it acknowledged?</label>
              <input [id]="tabIdPrefix + '-note'" class="note-input" maxlength="500"
                     placeholder="Why (e.g. spare port, planned change)"
                     [value]="noteEd.note" (input)="noteEd.note = $any($event.target).value">
              <label class="sr-only" [for]="tabIdPrefix + '-by'">Your name</label>
              <input [id]="tabIdPrefix + '-by'" class="note-by" maxlength="80" placeholder="Your name"
                     [readOnly]="noteEd.byFixed"
                     [value]="noteEd.by" (input)="noteEd.by = $any($event.target).value">
              <button type="submit" class="btn primary" [disabled]="noteEd.saving">Save</button>
              <button type="button" class="btn" (click)="cancelNote()">Cancel</button>
              <span class="note-err" role="alert" *ngIf="noteEd.error">{{ noteEd.error }}</span>
            </form>
          </span>
        </div>
      </ng-container>
    </div>
  </div>
  <div class="card span6" id="fleet-licenses">
    <div class="card-header">{{ hasCertRows ? 'Licenses & certificates to renew' : 'Licenses to renew' }}
      <span class="hdr-note">expired, or expiring within 90 days<span
        *ngIf="hasCertRows"> (certificates: {{ thresholds.cert_days.warning }} days)</span></span>
    </div>
    <div class="tbl-wrap fleet-lic-wrap">
      <div class="ok" *ngIf="!licenses.length">
        <span class="label label-success">Nothing expires within 90 days</span>
      </div>
      <table class="clr sortable" *ngIf="licenses.length">
        <thead>
          <tr>
            <th [attr.aria-sort]="licenseSort.ariaSort('device')">
              <button type="button" class="sort-btn" (click)="sortTable(licenseSort, 'device')">Device <span class="sort-mark" aria-hidden="true">{{ licenseSort.mark('device') }}</span></button></th>
            <th [attr.aria-sort]="licenseSort.ariaSort('name')">
              <button type="button" class="sort-btn" (click)="sortTable(licenseSort, 'name')">{{ hasCertRows ? 'License or certificate' : 'License' }} <span class="sort-mark" aria-hidden="true">{{ licenseSort.mark('name') }}</span></button></th>
            <th class="num" [attr.aria-sort]="licenseSort.ariaSort('expires')">
              <button type="button" class="sort-btn" (click)="sortTable(licenseSort, 'expires')">Expires <span class="sort-mark" aria-hidden="true">{{ licenseSort.mark('expires') }}</span></button></th>
          </tr>
        </thead>
        <tbody>
          <tr *ngFor="let license of licenses; trackBy: trackByLicense">
            <td><button type="button" class="dev-link" (click)="open(license.serial)">{{ license.device }}</button></td>
            <td>{{ license.name }} <span class="label label-info" *ngIf="license.kind === 'certificate'">certificate</span><span
              class="dim"> {{ license.status }}</span></td>
            <td class="num">
              <span class="label" [ngClass]="licenseBadge(license).cls">{{ licenseBadge(license).text }}</span>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  </div>

  <!-- State changes of every device, newest first -->
  <div class="card span12" id="fleet-events" *ngIf="eventsSupported">
    <div class="card-header has-tabs">
      <span class="card-title">Recent events across devices</span>
      <span class="chips" role="group" aria-label="Event kind" *ngIf="eventKinds.length > 1">
        <button type="button" class="chip" [attr.aria-pressed]="eventKind === 'all'"
                (click)="setEventKind('all')">All</button>
        <button type="button" class="chip" *ngFor="let kind of eventKinds; trackBy: trackByKind"
                [attr.aria-pressed]="eventKind === kind"
                (click)="setEventKind(kind)">{{ eventKindLabel[kind] }}</button>
      </span>
      <span class="hdr-note" *ngIf="eventsMatching > events.length">newest {{ events.length }}
        of {{ eventsMatching }}</span>
    </div>
    <div class="events-list">
      <div class="empty" *ngIf="!events.length">No state change {{ filtered ? 'on the devices shown' : '' }} in this window.</div>
      <div class="event" *ngFor="let event of events; trackBy: trackByEvent">
        <span class="event-time" [title]="eventWhen(event)">{{ fmtStamp(event.t) }}</span>
        <span class="label" [ngClass]="eventBadge(event.sev).cls">{{ eventBadge(event.sev).text }}</span>
        <button type="button" class="dev-link small event-device" (click)="open(event.serial)">{{ event.device }}</button>
        <span class="event-title">{{ event.title }}</span>
        <button type="button" class="link-btn ev-link" *ngIf="eventHasEvidence(event)"
                [attr.aria-label]="'Show the data behind: ' + event.title"
                (click)="showEvent(event)">Show</button>
      </div>
    </div>
  </div>

  <!-- SD-WAN members of every device, coloured by the chosen metric -->
  <div class="card span12" id="fleet-sdwan" *ngIf="showSdwan">
    <div class="card-header has-tabs">
      <span class="card-title">SD-WAN health across devices</span>
      <span class="btn-group btn-group-sm" role="group" aria-label="Metric">
        <button type="button" [class.active]="sdwanMetric === 'loss'"
                [attr.aria-pressed]="sdwanMetric === 'loss'"
                (click)="setSdwanMetric('loss')">Loss</button>
        <button type="button" [class.active]="sdwanMetric === 'latency'"
                [attr.aria-pressed]="sdwanMetric === 'latency'"
                (click)="setSdwanMetric('latency')">Latency</button>
        <button type="button" [class.active]="sdwanMetric === 'jitter'"
                [attr.aria-pressed]="sdwanMetric === 'jitter'"
                (click)="setSdwanMetric('jitter')">Jitter</button>
      </span>
      <span class="hdr-note">latest health-check result per member; red is a member down</span>
    </div>
    <div class="tbl-wrap">
      <div class="empty" *ngIf="!sdwanRows.length">No matching device uses SD-WAN.</div>
      <!-- a member column sorts on the metric shown (Loss, Latency or Jitter) -->
      <table class="clr heatmap sortable" *ngIf="sdwanRows.length">
        <thead>
          <tr>
            <th [attr.aria-sort]="sdwanSort.ariaSort('device')">
              <button type="button" class="sort-btn" (click)="sortTable(sdwanSort, 'device')">Device <span class="sort-mark" aria-hidden="true">{{ sdwanSort.mark('device') }}</span></button></th>
            <th class="num" *ngFor="let member of sdwanMembers; trackBy: trackByText"
                [attr.aria-sort]="sdwanSort.ariaSort(sdwanKey(member))">
              <button type="button" class="sort-btn" (click)="sortTable(sdwanSort, sdwanKey(member))">{{ member }} <span class="sort-mark" aria-hidden="true">{{ sdwanSort.mark(sdwanKey(member)) }}</span></button></th>
          </tr>
        </thead>
        <tbody>
          <tr *ngFor="let sdwanRow of sdwanRows; trackBy: trackBySdwan">
            <td><button type="button" class="dev-link" (click)="open(sdwanRow.serial)">{{ sdwanRow.device }}</button></td>
            <td class="num hm-cell" *ngFor="let cell of sdwanRow.cells; trackBy: trackByIndex"
                [ngClass]="cell.cls" [title]="cell.title">{{ cell.text }}</td>
          </tr>
        </tbody>
      </table>
    </div>
  </div>

  <div class="card span8">
    <div class="card-header">Traffic per day, stacked by device
      <span class="hdr-note">last 31 days of full history; transit traffic counts
        on both its interfaces</span>
    </div>
    <div class="card-block"><div id="fl-daily" class="chart chart-daily"></div></div>
  </div>
  <div class="card span4">
    <div class="card-header">FortiOS versions in use
      <span class="hdr-note">newest highlighted</span>
    </div>
    <div class="card-block"><div id="fl-versions" class="chart chart-daily"></div></div>
  </div>

  <!-- Policy cleanup candidates, merged across devices -->
  <div class="card span12" id="fleet-hygiene" *ngIf="hygieneSupported">
    <div class="card-header">Policy cleanup
      <span class="hdr-note">unused policies (0 hits) and policies too large to offload</span>
    </div>
    <div class="tbl-wrap">
      <div class="ok" *ngIf="!hygiene.length">
        <span class="label label-success">No cleanup candidate{{ filtered ? ' on the devices shown' : '' }}</span>
      </div>
      <table class="clr sortable" *ngIf="hygiene.length">
        <thead>
          <tr>
            <th [attr.aria-sort]="hygieneSort.ariaSort('device')">
              <button type="button" class="sort-btn" (click)="sortTable(hygieneSort, 'device')">Device <span class="sort-mark" aria-hidden="true">{{ hygieneSort.mark('device') }}</span></button></th>
            <th class="num" [attr.aria-sort]="hygieneSort.ariaSort('total')">
              <button type="button" class="sort-btn" (click)="sortTable(hygieneSort, 'total')">Policies <span class="sort-mark" aria-hidden="true">{{ hygieneSort.mark('total') }}</span></button></th>
            <th class="num" [attr.aria-sort]="hygieneSort.ariaSort('unused')">
              <button type="button" class="sort-btn" (click)="sortTable(hygieneSort, 'unused')">Unused <span class="sort-mark" aria-hidden="true">{{ hygieneSort.mark('unused') }}</span></button></th>
            <th class="num" [attr.aria-sort]="hygieneSort.ariaSort('oversize')">
              <button type="button" class="sort-btn" (click)="sortTable(hygieneSort, 'oversize')">Oversize <span class="sort-mark" aria-hidden="true">{{ hygieneSort.mark('oversize') }}</span></button></th>
            <th [attr.aria-sort]="hygieneSort.ariaSort('candidates')">
              <button type="button" class="sort-btn" (click)="sortTable(hygieneSort, 'candidates')">Candidates <span class="sort-mark" aria-hidden="true">{{ hygieneSort.mark('candidates') }}</span></button></th>
          </tr>
        </thead>
        <tbody>
          <tr *ngFor="let entry of hygiene; trackBy: trackByHygiene">
            <td><button type="button" class="dev-link" (click)="open(entry.serial)">{{ entry.device }}</button></td>
            <td class="num dim">{{ entry.total }}</td>
            <td class="num" [class.util-warn]="entry.unused_count">{{ entry.unused_count }}</td>
            <td class="num" [class.util-warn]="entry.oversize_count">{{ entry.oversize_count }}</td>
            <td class="wrap">
              <ng-container *ngIf="hygieneOpen.has(entry.serial); else short">
                <div *ngIf="entry.unused.length"><span class="dim">Unused:</span> {{ entry.unused.join(', ') }}<span
                  class="dim" *ngIf="entry.unused_count > entry.unused.length"> and {{ entry.unused_count - entry.unused.length }} more</span></div>
                <div *ngIf="entry.oversize.length"><span class="dim">Oversize:</span> {{ entry.oversize.join(', ') }}<span
                  class="dim" *ngIf="entry.oversize_count > entry.oversize.length"> and {{ entry.oversize_count - entry.oversize.length }} more</span></div>
              </ng-container>
              <ng-template #short>
                <span class="clip">{{ entry.unused.concat(entry.oversize).slice(0, 3).join(', ') }}</span>
              </ng-template>
              <button type="button" class="link-btn" *ngIf="entry.unused_count + entry.oversize_count > 3 || hygieneOpen.has(entry.serial)"
                      [attr.aria-expanded]="hygieneOpen.has(entry.serial)"
                      (click)="toggleHygiene(entry.serial)">{{ hygieneOpen.has(entry.serial) ? 'Show less' : 'Show all ' + (entry.unused_count + entry.oversize_count) }}</button>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  </div>
  </ng-container>
</div>
